import PropTypes from 'prop-types';
import React, { useState, Fragment, useRef, useEffect, useMemo } from 'react';
import { List, Record } from 'immutable';
import { format } from 'date-fns';
import classNames from 'classnames';
import _ from 'lodash';

import scrollElementBy from '@xanadu/ui-shared/utils/scrollElementBy';

import { PRODUCTS, PRODUCT_ROUTES, RACING_SPORTS } from '../../../constants';

import refreshCacheAndReload from '../../../utils/pageReload';

import useSports from '@xanadu/ui-shared/hooks/useSports';

import Scrollable from '@xanadu/ui-shared/components/widgets/Scrollable';
import NavItem from '../NavItem';

import IconChevronDown from '../../../../images/icons/misc/icon-d-down.svg';
import IconChevronUp from '../../../../images/icons/misc/icon-d-up.svg';
import IconUpdate from '../../../../images/icons/misc/icon-update.svg';

import styles from './NavMain.module.scss';

const NavMain = ({
  tags,
  product,
  topFive,
  loading,
  uiVersion,
  virtualsAllowed,
  casinoLobbies,
  liveCasinoLobbies,
  virtualLobbies,
  cinemaMode,
  pendingUIUpdate,
  casinoVisibilityRestricted,
  inPlayTags,
}) => {
  const [listScrolledTo, setListScrollPosition] = useState('');
  const listItems = useRef();
  const iconUpdateRef = useRef();
  const iconUpdateBackgroundRef = useRef();

  const isExchange = product === PRODUCTS.exchange;

  const showSports = [PRODUCTS.mbzero, PRODUCTS.exchange].includes(product);

  const productUrlPrefix = PRODUCT_ROUTES[product];

  const allSports = useSports(tags, isExchange);

  const topFiveSports = useMemo(() => (
    topFive.reduce((acc, topFiveSport) => {
      const tagFound = tags.find(tag => tag.urlName === topFiveSport.urlName) || new Record();
      const { id, url, metaTags = [], urlName } = tagFound;

      const { url: firstSubMetaTagUrlName } = tags.find(t => t.uuid === metaTags[0]) || {};

      const setFirstCompetitionUrl = !RACING_SPORTS.includes(urlName) && metaTags.length === 1;

      return id ? [
        ...acc,
        tagFound.set('url', setFirstCompetitionUrl ? firstSubMetaTagUrlName : url)
      ] : acc;
    }, [])
  ), [topFive, tags]);

  const scrollListItems = (scrollValue) => {
    const { current } = listItems;

    if (current) {
      scrollElementBy(current, 'top', scrollValue);
    }
  };

  const handleListScroll = _.throttle(() => {
    const {
      scrollHeight,
      scrollTop,
      offsetHeight
    } = listItems.current || {};

    if (!scrollHeight) {
      return;
    }

    // for some reasons on Windows the difference is of 1px
    // so we subtract 2 from the scrollHeight
    if ((scrollTop + offsetHeight) >= (scrollHeight - 2) && scrollTop) {
      setListScrollPosition('bottom');
    } else if (!scrollTop && offsetHeight < scrollHeight) {
      setListScrollPosition('top');
    } else if (offsetHeight < scrollHeight) {
      setListScrollPosition('middle');
    } else {
      setListScrollPosition('');
    }
  }, 400);

  const onFooterClick = () => {
    if (pendingUIUpdate) {
      iconUpdateRef.current.classList.add(styles['iconUpdate--spinning']);

      // No need to clear as the page will refresh
      setTimeout(() => {
        refreshCacheAndReload();
      }, 800);
    }
  };

  useEffect(() => {
    let timeout;

    if (listItems.current) {
      timeout = setTimeout(() => {
        handleListScroll({});
      }, 2000);

      listItems.current.addEventListener('scroll', handleListScroll);
      window.addEventListener('resize', handleListScroll);
    }

    return () => {
      clearTimeout(timeout);

      if (listItems.current) {
        listItems.current.removeEventListener('scroll', handleListScroll);
      }

      window.removeEventListener('resize', handleListScroll);
    };
  }, []);

  useEffect(() => {
    let timer;

    if (pendingUIUpdate && iconUpdateRef.current) {
      iconUpdateRef.current.classList.add(styles['iconUpdate--animate']);
      iconUpdateBackgroundRef.current.classList.add(styles['iconUpdateBackground--animate']);

      timer = setTimeout(() => {
        iconUpdateRef.current.classList.add(styles['iconUpdate--pulse']);
        iconUpdateBackgroundRef.current.classList.remove(styles['iconUpdateBackground--animate']);
      }, 800);
    }

    return () => {
      clearTimeout(timer);
    };
  }, [pendingUIUpdate]);

  const currentTime = (
    <span
      className={`${styles.time}`}
    >{format(new Date(), 'HH:mm')}</span>
  );

  const currentDate = (
    <span
      className={`${styles.date}`}
    >{format(new Date(), 'dd MMM yyyy')}</span>
  );

  const topButtonScrollClasses = [
    styles.buttonForScroll,
    styles['buttonForScroll--top'],
    (listScrolledTo === 'top' || !listScrolledTo) ? styles['buttonForScroll--hide'] : ''
  ];

  const bottomButtonScrollClasses = [
    styles.buttonForScroll,
    styles['buttonForScroll--bottom'],
    (listScrolledTo === 'bottom' || !listScrolledTo) ? styles['buttonForScroll--hide'] : ''
  ];

  const mainClasses = classNames({
    [styles.main]: true,
    [styles['main--cinemaMode']]: cinemaMode,
  });

  const footerClass = classNames({
    [styles.footer]: true,
    [styles['footer--withPendingUpdate']]: pendingUIUpdate,
  });

  return (
    <div className={mainClasses}>
      <Scrollable>
        <div className={`${styles.items}`} ref={listItems}>
          {loading ? (
            <div>
              {isExchange && (
                <Fragment>
                  <div className={`${styles.navSectionTitle}`}>Popular</div>
                  <div className={styles.placeholder}>
                    <div className={styles.placeholderStripe} />
                  </div>
                  <div className={styles.placeholder}>
                    <div className={styles.placeholderStripe} />
                  </div>
                  <div className={styles.placeholder}>
                    <div className={styles.placeholderStripe} />
                  </div>
                  <div className={`${styles.dividerContainer}`}>
                    <div className={`${styles.divider}`} />
                  </div>
                </Fragment>
              )}
              <div className={`${styles.navSectionTitle}`}>All Sports</div>
              <div className={styles.placeholder}>
                <div className={styles.placeholderStripe} />
              </div>
              <div className={styles.placeholder}>
                <div className={styles.placeholderStripe} />
              </div>
              <div className={styles.placeholder}>
                <div className={styles.placeholderStripe} />
              </div>
              <div className={styles.placeholder}>
                <div className={styles.placeholderStripe} />
              </div>
              <div className={styles.placeholder}>
                <div className={styles.placeholderStripe} />
              </div>
            </div>
          ) : (
            <Fragment>
              {isExchange && (
                <Fragment>
                  <div className={`${styles.navSectionTitle}`}>Popular</div>
                  {topFiveSports.map((topFiveTag) => (
                    <NavItem
                      key={`topFive-${topFiveTag.id}`}
                      name={topFiveTag.name}
                      iconName={topFiveTag.urlName}
                      url={`${productUrlPrefix}/events${topFiveTag.url}`}
                    />
                  ))}
                  {
                    inPlayTags.size ?
                      <div>
                        <NavItem
                          name="In-Play Betting"
                          url="/events/live-betting"
                          iconName="live-betting"
                        />
                      </div> : null
                  }
                  <div className={`${styles.dividerContainer}`}>
                    <div className={`${styles.divider}`} />
                  </div>
                </Fragment>
              )}
              {[PRODUCTS.casino, PRODUCTS.liveCasino, PRODUCTS.virtuals].includes(product) && (
                <Fragment>
                  {!casinoVisibilityRestricted && (
                    <Fragment>
                      <div className={`${styles.navSectionTitle}`}>Casino</div>
                      {casinoLobbies && casinoLobbies.map((lobby) => (
                        <NavItem
                          name={lobby.name}
                          url={`/casino/${lobby.id}`}
                          key={lobby.id}
                          iconName="casino"
                        />
                      ))}
                      <div className={`${styles.dividerContainer}`}>
                        <div className={`${styles.divider}`} />
                      </div>
                      <div className={`${styles.navSectionTitle}`}>Live Casino</div>
                      {liveCasinoLobbies && liveCasinoLobbies.map((lobby) => (
                        <NavItem
                          name={lobby.name}
                          url={`/live-casino/${lobby.id}`}
                          key={lobby.id}
                          iconName="casino"
                        />
                      ))}
                      <div className={`${styles.dividerContainer}`}>
                        <div className={`${styles.divider}`} />
                      </div>
                    </Fragment>
                  )}
                  {virtualsAllowed && (
                    <Fragment>
                      <div className={`${styles.navSectionTitle}`}>Virtuals</div>
                      {virtualLobbies && virtualLobbies.map((lobby) => (
                        <NavItem
                          name={lobby.name}
                          iconName="virtuals"
                          key={lobby.id}
                          url={`/virtuals/${lobby.id}`}
                        />
                      ))}
                    </Fragment>
                  )}
                </Fragment>
              )}
              {showSports && (
                <Fragment>
                  <div className={`${styles.navSectionTitle}`}>All Sports</div>
                  {
                    allSports.map(({
                      uuid,
                      name,
                      urlName,
                      url
                    }) => (
                      <NavItem
                        key={uuid}
                        name={name}
                        iconName={urlName}
                        url={urlName === 'colossus' ? '/colossus' : `${productUrlPrefix}/events${url}`}
                      />
                    ))
                  }
                </Fragment>
              )}
            </Fragment>
          )}
        </div>
      </Scrollable>
      <div
        className={topButtonScrollClasses.join(' ')}
        onClick={() => scrollListItems(-150)}
        role="button"
      >
        <IconChevronUp className={styles.buttonForScrollIcon} />
      </div>
      <div
        className={bottomButtonScrollClasses.join(' ')}
        onClick={() => scrollListItems(150)}
        role="button"
      >
        <IconChevronDown className={styles.buttonForScrollIcon} />
      </div>
      <div
        className={footerClass}
        {...(pendingUIUpdate && {
          onClick: onFooterClick,
          role: 'button'
        })}
      >
        <span ref={iconUpdateBackgroundRef} className={styles.iconUpdateBackground}>
          <span>Click to Update</span>
        </span>
        <span className={styles.uiVersion}>{uiVersion}</span>
        <span className={styles.date}>
          {currentTime} <i>|</i> {currentDate}
        </span>
        <span ref={iconUpdateRef} className={styles.iconUpdate}>
          <IconUpdate />
        </span>
      </div>
    </div>
  );
};

NavMain.propTypes = {
  tags: PropTypes.instanceOf(List),
  topFive: PropTypes.instanceOf(List),
  cinemaMode: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  uiVersion: PropTypes.string.isRequired,
  pendingUIUpdate: PropTypes.bool.isRequired,
  virtualsAllowed: PropTypes.bool.isRequired,
  product: PropTypes.string.isRequired,
  casinoLobbies: PropTypes.instanceOf(Object).isRequired,
  liveCasinoLobbies: PropTypes.instanceOf(Object).isRequired,
  virtualLobbies: PropTypes.instanceOf(Object).isRequired,
  casinoVisibilityRestricted: PropTypes.bool,
  inPlayTags: PropTypes.instanceOf(List).isRequired,
};

export default NavMain;
